import React, { useState } from 'react'
import { Button, Input, Card, Space, Typography, message } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import {
  downloadCertificateWithPosition,
  downloadCertificateAuto,
} from '../../../utils/helpers'

const { Title, Text } = Typography

const SimpleCertificateTest: React.FC = () => {
  const [name, setName] = useState('John Doe')
  const [courseName, setCourseName] = useState('React Development Fundamentals')
  const [loading, setLoading] = useState(false)

  const handleDownload = async () => {
    if (!name.trim()) {
      message.error('Nama harus diisi')
      return
    }

    try {
      setLoading(true)
      await downloadCertificateWithPosition({
        name: name.trim(),
        // x: undefined, // Auto-centered based on text length
        y: 400,
        courseName: 'React Development Fundamentals',
        // courseX: undefined, // Auto-centered based on text length
        courseY: 320,
        fontSize: 24,
        fontColor: { r: 42, g: 170, b: 250 },
        courseFontSize: 18,
        courseFontColor: { r: 51, g: 51, b: 51 },
      })
      message.success('Sertifikat berhasil diunduh!')
    } catch (error) {
      message.error('Gagal mengunduh sertifikat')
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAutoDownload = async () => {
    if (!name.trim() || !courseName.trim()) {
      message.error('Nama dan nama course harus diisi')
      return
    }

    setLoading(true)
    try {
      await downloadCertificateAuto(name, courseName, undefined, {
        courseY: 400, // Test specific course Y position
        enableTextWrapping: true,
      })
      message.success('Sertifikat auto-generated berhasil diunduh')
    } catch (error) {
      message.error('Gagal mengunduh sertifikat')
      console.error('Auto download error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleTestCoursePosition = async () => {
    if (!name.trim() || !courseName.trim()) {
      message.error('Nama dan nama course harus diisi')
      return
    }

    setLoading(true)
    try {
      // Test with different course Y positions
      await downloadCertificateAuto(
        name,
        courseName,
        'test-course-position.pdf',
        {
          courseY: 450, // Higher position
          enableTextWrapping: true,
        },
      )
      message.success('Test course position berhasil')
    } catch (error) {
      message.error('Gagal test course position')
      console.error('Test error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleTestWithTopics = async () => {
    if (!name.trim() || !courseName.trim()) {
      message.error('Nama dan nama course harus diisi')
      return
    }

    setLoading(true)
    try {
      // Sample topics for testing
      const sampleTopics = [
        { name: 'Memahami konsep dasar sediaan obat steril.' },
        {
          name:
            'Menguasai prinsip dan teknik Aseptic Dispensing sesuai standar kefarmasian.',
        },
        {
          name:
            'Memahami aspek Good Manufacturing Practice (GMP) dan Good Pharmacy Practice (GPP) dalam pengelolaan obat steril.',
        },
      ]

      await downloadCertificateAuto(
        name,
        courseName,
        'test-with-topics.pdf',
        {
          courseY: 400,
          enableTextWrapping: true,
        },
        sampleTopics,
      )
      message.success('Test certificate with topics berhasil diunduh!')
    } catch (error) {
      message.error('Gagal test certificate dengan topics')
      console.error('Test topics error:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
      <Card>
        <Title level={3}>Test Sertifikat Sederhana</Title>
        <Text type="secondary">
          Masukkan nama dan klik download untuk mengunduh sertifikat dengan
          posisi default
        </Text>

        <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
          <div>
            <Text strong>Nama:</Text>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Masukkan nama"
              style={{ marginTop: 8 }}
            />
          </div>

          <div>
            <Text strong>Nama Course:</Text>
            <Input
              value={courseName}
              onChange={(e) => setCourseName(e.target.value)}
              placeholder="Masukkan nama course"
              style={{ marginTop: 8 }}
            />
          </div>

          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            loading={loading}
            size="large"
            style={{ width: '100%' }}
          >
            Download Manual
          </Button>

          <Button
            type="default"
            icon={<DownloadOutlined />}
            onClick={handleAutoDownload}
            loading={loading}
            size="large"
            style={{ width: '100%' }}
          >
            Download Auto-Generated
          </Button>

          <Button
            type="dashed"
            icon={<DownloadOutlined />}
            onClick={handleTestCoursePosition}
            loading={loading}
            size="large"
            style={{ width: '100%' }}
          >
            Test Course Position (Y: 450)
          </Button>

          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleTestWithTopics}
            loading={loading}
            size="large"
            style={{
              width: '100%',
              backgroundColor: '#52c41a',
              borderColor: '#52c41a',
            }}
          >
            🆕 Test With Topics Table
          </Button>
        </Space>

        <div
          style={{
            marginTop: 16,
            padding: 16,
            backgroundColor: '#f5f5f5',
            borderRadius: 8,
          }}
        >
          <Text strong>Posisi Default:</Text>
          <ul style={{ marginTop: 8 }}>
            <li>X: Auto-center (tengah berdasarkan panjang nama)</li>
            <li>Y: 400pt (tengah vertical)</li>
            <li>Font Size: 24pt</li>
            <li>Color: Hitam</li>
          </ul>
        </div>

        <div
          style={{
            marginTop: 16,
            padding: 16,
            backgroundColor: '#e6f7ff',
            borderRadius: 8,
            border: '1px solid #91d5ff',
          }}
        >
          <Text strong>🆕 Topics Table Feature:</Text>
          <ul style={{ marginTop: 8 }}>
            <li>Menampilkan tabel daftar topik di dalam sertifikat</li>
            <li>Header dengan background biru dan teks putih</li>
            <li>Setiap topik ditampilkan dengan bullet point</li>
            <li>Alternating row colors untuk readability</li>
            <li>Posisi otomatis di bawah nama course</li>
          </ul>
        </div>
      </Card>
    </div>
  )
}

export default SimpleCertificateTest
